import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  AppBar, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Container, 
  Grid,
  Paper, 
  Toolbar, 
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import QuizIcon from '@mui/icons-material/Quiz';
import ArticleIcon from '@mui/icons-material/Article';
import CloseIcon from '@mui/icons-material/Close';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CodeIcon from '@mui/icons-material/Code';
import TableChartIcon from '@mui/icons-material/TableChart';
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import TimelineIcon from '@mui/icons-material/Timeline';
import WidgetsIcon from '@mui/icons-material/Widgets';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { executeWorkflow } from './services/api';
import { QuizComponent, TrainingDisplay, parseTrainingContent } from './components/QuizComponent';
import { generateSimulation, createHistoryEntry } from './services/backendApi';
import { HistoryPanel } from './components/HistoryPanel';
import EquipmentManager from './components/EquipmentManager';
import SensorImport from './components/SensorImport';
import SensorDocumentation from './components/SensorDocumentation';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';
import { LandingPage } from './pages/LandingPage';
import { ContactPage } from './pages/ContactPage';
import { ImpressumPage, PrivacyPage } from './pages/LegalPages';
import {
  ContentType,
  DocumentationData,
  TrainingData,
  SimulationData,
  TemplatesData,
  ValidationErrors,
  validateDocumentation,
  validateTraining,
  validateSimulation,
  validateTemplates,
  defaultDocumentation,
  defaultTraining,
  defaultSimulation,
  defaultTemplates,
  DocumentationForm,
  TrainingForm,
  SimulationForm,
  TemplatesForm,
} from './components/ContentForms';

// Industrial professional theme
const industrialTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00b4d8',
      light: '#00d9ff',
      dark: '#0096c7',
    },
    secondary: {
      main: '#14b8a6',
      light: '#2dd4bf',
      dark: '#0d9488',
    },
    background: {
      default: '#0a1929',
      paper: '#102a43',
    },
    text: {
      primary: '#e2e8f0',
      secondary: '#94a3b8',
    },
    divider: 'rgba(0, 180, 216, 0.12)',
    error: {
      main: '#f43f5e',
    },
    warning: {
      main: '#f97316',
    },
    success: {
      main: '#10b981',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600, letterSpacing: '0.02em' },
    button: { fontWeight: 600, letterSpacing: '0.02em' },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: 'linear-gradient(135deg, #0a1929 0%, #102a43 50%, #0a1929 100%)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, rgba(10, 25, 41, 0.95), rgba(16, 42, 67, 0.95))',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 180, 216, 0.2)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, rgba(16, 42, 67, 0.9), rgba(36, 59, 83, 0.9))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 180, 216, 0.15)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, rgba(16, 42, 67, 0.8), rgba(36, 59, 83, 0.8))',
          border: '1px solid rgba(0, 180, 216, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          borderRadius: 8,
        },
        contained: {
          background: 'linear-gradient(135deg, #00b4d8, #0096c7)',
          boxShadow: '0 4px 15px rgba(0, 180, 216, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #00d9ff, #00b4d8)',
            boxShadow: '0 6px 20px rgba(0, 180, 216, 0.4)',
          },
        },
        outlined: {
          borderColor: 'rgba(0, 180, 216, 0.5)',
          '&:hover': {
            borderColor: '#00b4d8',
            backgroundColor: 'rgba(0, 180, 216, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(10, 25, 41, 0.5)',
            '& fieldset': {
              borderColor: 'rgba(0, 180, 216, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 180, 216, 0.4)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00b4d8',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
        filled: {
          background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2), rgba(20, 184, 166, 0.2))',
          border: '1px solid rgba(0, 180, 216, 0.3)',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: '#94a3b8',
          borderColor: 'rgba(0, 180, 216, 0.2)',
          textTransform: 'none',
          fontWeight: 500,
          padding: '12px 20px',
          '&.Mui-selected': {
            color: '#00d9ff',
            backgroundColor: 'rgba(0, 180, 216, 0.15)',
            borderColor: 'rgba(0, 180, 216, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 180, 216, 0.25)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 180, 216, 0.08)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 180, 216, 0.12)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'linear-gradient(135deg, rgba(16, 42, 67, 0.98), rgba(36, 59, 83, 0.98))',
          border: '1px solid rgba(0, 180, 216, 0.2)',
        },
      },
    },
  },
});

const queryClient = new QueryClient();

// Types for generated content
interface FormatContent {
  format: string;
  filename: string;
  content: string;
  mimeType: string;
}

interface GeneratedContent {
  id: string;
  contentType: string;
  title: string;
  content: string;
  formats: string[];
  generatedAt: string;
  metadata?: any;
  multiFormat?: FormatContent[]; // Für Bulk-Import mit mehreren Formaten
}

const normalizeFormatName = (format: string): string => {
  return (format || '').toLowerCase().replace(/[^a-z0-9]/g, '');
};

const decodeXmlEntitiesIfNeeded = (content: string): string => {
  if (!content) return content;
  // Heuristic: some responses contain an XML document escaped into a <p> with &lt;...&gt;
  if (content.includes('&lt;') && content.includes('&gt;') && content.includes('&lt;?xml')) {
    return content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }
  return content;
};

const toFormatContentArray = (items: unknown): FormatContent[] | undefined => {
  if (!Array.isArray(items)) return undefined;
  const mapped = items
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return null;
      const obj = raw as Record<string, unknown>;
      const formatValue = obj.format ?? obj.formatName ?? obj.type;
      if (typeof formatValue !== 'string') return null;
      const filenameValue = typeof obj.filename === 'string' ? obj.filename : `${formatValue}.${(obj.extension as string) || 'txt'}`;
      const contentValue = typeof obj.content === 'string'
        ? obj.content
        : typeof obj.body === 'string'
          ? obj.body
          : typeof obj.data === 'string'
            ? obj.data
            : typeof obj.payload === 'string'
              ? obj.payload
              : '';
      const mimeValue = typeof obj.mimeType === 'string' ? obj.mimeType : typeof obj.mime === 'string' ? obj.mime : 'text/plain';
      return {
        format: formatValue,
        filename: filenameValue,
        content: contentValue,
        mimeType: mimeValue,
      } as FormatContent;
    })
    .filter((entry): entry is FormatContent => Boolean(entry && entry.format));
  return mapped.length > 0 ? mapped : undefined;
};

const sanitizeMultiFormatEntries = (entries: FormatContent[] | undefined): FormatContent[] | undefined => {
  if (!entries || entries.length === 0) return undefined;

  return entries.map((entry) => {
    const rawFormat = typeof entry.format === 'string'
      ? entry.format.trim()
      : String(entry.format ?? '');
    const normalized = normalizeFormatName(rawFormat);
    const filename =
      typeof entry.filename === 'string' && entry.filename.trim().length > 0
        ? entry.filename.trim()
        : `${rawFormat || 'output'}.${normalized.includes('dita') || normalized.includes('xml') || normalized.includes('aasx') ? 'xml' : normalized.includes('vdi') ? 'md' : 'txt'}`;
    const defaultMime =
      entry.mimeType ||
      (normalized.includes('dita') || normalized.includes('xml') || normalized.includes('aasx')
        ? 'application/xml'
        : normalized.includes('vdi')
          ? 'text/markdown'
          : 'text/plain');

    return {
      ...entry,
      format: rawFormat,
      filename,
      mimeType: defaultMime,
      content: decodeXmlEntitiesIfNeeded(entry.content ?? ''),
    };
  });
};

const parseMultiFormatFromString = (rawContent: string | undefined): FormatContent[] | undefined => {
  if (!rawContent) return undefined;

  const cleaned = rawContent
    .replace(/```[a-zA-Z0-9]*\s*/g, '')
    .replace(/```/g, '')
    .trim();

  const segments = cleaned.split(/---FORMAT:/i).slice(1);
  if (segments.length === 0) return undefined;

  const results: FormatContent[] = [];

  for (const segment of segments) {
    const trimmed = segment.trim();
    const markerEnd = trimmed.indexOf('---');
    if (markerEnd === -1) continue;

    const formatLabel = trimmed.slice(0, markerEnd).trim();
    const body = trimmed.slice(markerEnd + 3).trim();
    if (!formatLabel || !body) continue;

    const normalized = normalizeFormatName(formatLabel);
    let extension = 'txt';
    let mimeType = 'text/plain';

    if (normalized.includes('dita') || normalized.includes('xml')) {
      extension = normalized.includes('dita') ? 'dita' : 'xml';
      mimeType = 'application/xml';
    } else if (normalized.includes('aasx')) {
      extension = 'xml';
      mimeType = 'application/xml';
    } else if (normalized.includes('vdi') || normalized.includes('markdown') || normalized.includes('md')) {
      extension = 'md';
      mimeType = 'text/markdown';
    }

    const safeFormat = formatLabel;
    const safeFilename = `${normalizeFormatName(safeFormat) || 'output'}.${extension}`;
    results.push({
      format: safeFormat,
      filename: safeFilename,
      content: body,
      mimeType,
    });
  }

  return results.length > 0 ? results : undefined;
};

const extractTextFromDataArray = (data: unknown): string | undefined => {
  if (!Array.isArray(data)) return undefined;
  const textParts: string[] = [];

  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const obj = item as Record<string, unknown>;
    const type = typeof obj.type === 'string' ? obj.type : undefined;
    if (type === 'thinking') {
      continue;
    }

    if (typeof obj.text === 'string') {
      textParts.push(obj.text);
    }

    if (Array.isArray(obj.data)) {
      const nested = extractTextFromDataArray(obj.data);
      if (nested) {
        textParts.push(nested);
      }
    }
  }

  return textParts.length > 0 ? textParts.join('\n') : undefined;
};

const mergeFormatEntries = (base: FormatContent[] | undefined, entries: FormatContent[] | undefined): FormatContent[] | undefined => {
  if (!entries || entries.length === 0) return base;
  const aggregated = base ? [...base] : [];

  const seenKeys = new Set<string>();

  for (const entry of entries) {
    if (!entry.format || !entry.content) continue;
    const key = `${normalizeFormatName(entry.format)}::${entry.filename}`;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    aggregated.push(entry);
  }

  return aggregated.length > 0 ? aggregated : base;
};

const extractMultiFormatFromResult = (result: unknown): FormatContent[] | undefined => {
  if (!result) return undefined;

  const collectFromObject = (obj: Record<string, unknown>): FormatContent[] | undefined => {
    let collected: FormatContent[] | undefined;

    const candidateArrays = [obj.files, obj.data, obj.result, obj.results, obj.outputs, obj.documents, obj.items];
    for (const arrayCandidate of candidateArrays) {
      collected = mergeFormatEntries(collected, toFormatContentArray(arrayCandidate));
    }

    if (Array.isArray(obj.files)) {
      for (const file of obj.files) {
        if (!file || typeof file !== 'object') continue;
        const fileObj = file as Record<string, unknown>;
        collected = mergeFormatEntries(collected, toFormatContentArray([fileObj]));
        collected = mergeFormatEntries(collected, parseMultiFormatFromString(typeof fileObj.content === 'string' ? fileObj.content : undefined));
        collected = mergeFormatEntries(collected, parseMultiFormatFromString(extractTextFromDataArray(fileObj.data)));
      }
    }

    collected = mergeFormatEntries(collected, parseMultiFormatFromString(typeof obj.content === 'string' ? obj.content : undefined));
    collected = mergeFormatEntries(collected, parseMultiFormatFromString(typeof obj.text === 'string' ? obj.text : undefined));
    collected = mergeFormatEntries(collected, parseMultiFormatFromString(typeof obj.response === 'string' ? obj.response : undefined));
    collected = mergeFormatEntries(collected, parseMultiFormatFromString(extractTextFromDataArray(obj.data)));

    return collected;
  };

  let aggregated: FormatContent[] | undefined = toFormatContentArray(result);

  if (typeof result === 'string') {
    aggregated = mergeFormatEntries(aggregated, parseMultiFormatFromString(result));
  }

  if (Array.isArray(result)) {
    for (const item of result) {
      if (!item) continue;
      if (typeof item === 'string') {
        aggregated = mergeFormatEntries(aggregated, parseMultiFormatFromString(item));
      } else if (typeof item === 'object') {
        const objEntries = collectFromObject(item as Record<string, unknown>);
        aggregated = mergeFormatEntries(aggregated, objEntries);
      }
    }
  } else if (typeof result === 'object') {
    const objEntries = collectFromObject(result as Record<string, unknown>);
    aggregated = mergeFormatEntries(aggregated, objEntries);
  }

  return sanitizeMultiFormatEntries(aggregated);
};

const pickMultiFormatContent = (
  multiFormat: FormatContent[],
  requestedFormat: string
): FormatContent | undefined => {
  const target = normalizeFormatName(requestedFormat);

  // 1) Prefer exact/normalized match on label
  const byLabel = multiFormat.find(f => normalizeFormatName(f.format) === target);
  if (byLabel) return byLabel;

  // 2) Fallback: detect by content signature (handles label mismatches)
  const isAasx = (c: string) => /<aas:environment\b|xmlns:aas=/.test(c);
  const isVdi = (c: string) => /^---\s*[\s\S]*\nvdi2770:\s*/m.test(c) || /^#\s/.test(c);
  const isDita = (c: string) => /<!DOCTYPE\s+topic\b|<topic\b/.test(c);

  if (target.includes('aasx')) {
    return multiFormat.find(f => isAasx(f.content || ''));
  }
  if (target.includes('vdi')) {
    return multiFormat.find(f => isVdi(f.content || ''));
  }
  if (target.includes('dita')) {
    return multiFormat.find(f => isDita(f.content || ''));
  }

  // 3) Last resort: first item
  return multiFormat[0];
};

// Format icons mapping
const formatIcons: Record<string, React.ReactNode> = {
  Slides: <SlideshowIcon />,
  PDF: <PictureAsPdfIcon />,
  Quiz: <QuizIcon />,
  DITA: <ArticleIcon />,
  S1000D: <ArticleIcon />,
  Markdown: <DescriptionIcon />,
  HTML: <DescriptionIcon />,
  Video: <SlideshowIcon />,
  Interactive: <QuizIcon />,
  JSON: <DataObjectIcon />,
  XML: <CodeIcon />,
  'OPC UA NodeSet': <CodeIcon />,
  CSV: <TableChartIcon />,
};

// Convert Markdown to HTML with proper formatting
const markdownToHtml = (markdown: string): string => {
  return markdown
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold and italic
    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Tables (basic)
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      const isHeader = cells.some(c => c.includes('---'));
      if (isHeader) return '';
      return '<tr>' + cells.map(c => `<td>${c.trim()}</td>`).join('') + '</tr>';
    })
    // Lists
    .replace(/^- (.*$)/gim, '<li>$1</li>')
    .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
};

// Download helper functions
const downloadFile = (content: string, filename: string, mimeType: string, extension: string) => {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.${extension}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const generateSlides = (content: string, title: string): string => {
  // Extract sections and create slide-based HTML
  const sections = content.split(/(?=^#{1,3} )/gm).filter(s => s.trim());
  const slides = sections.map((section, i) => {
    const lines = section.trim().split('\n');
    const heading = lines[0]?.replace(/^#+\s*/, '') || `Folie ${i + 1}`;
    const body = lines.slice(1).join('\n');
    return `
<!-- Folie ${i + 1} -->
<div class="slide">
  <h2>${heading}</h2>
  <div class="slide-content">
    ${markdownToHtml(body)}
  </div>
  <div class="slide-number">${i + 1}</div>
</div>`;
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <title>${title} - Präsentation</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #1a1a2e; color: #eee; }
    .slide { 
      min-height: 100vh; padding: 60px; 
      border-bottom: 2px solid #16213e;
      page-break-after: always;
    }
    .slide h2 { color: #4fc3f7; font-size: 2.5em; margin-bottom: 40px; border-bottom: 3px solid #4fc3f7; padding-bottom: 15px; }
    .slide-content { font-size: 1.4em; line-height: 1.8; }
    .slide-content li { margin: 15px 0; padding-left: 30px; }
    .slide-content strong { color: #81d4fa; }
    .slide-content table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    .slide-content td { border: 1px solid #4fc3f7; padding: 15px; }
    .slide-number { position: fixed; bottom: 20px; right: 30px; color: #666; font-size: 1.2em; }
    @media print {
      .slide { page-break-after: always; }
      .slide-number { position: absolute; }
    }
  </style>
</head>
<body>
  <div class="slide title-slide">
    <h1 style="font-size: 3em; color: #4fc3f7; text-align: center; margin-top: 30vh;">${title}</h1>
    <p style="text-align: center; font-size: 1.5em; margin-top: 30px;">Generiert am ${new Date().toLocaleDateString('de-DE')}</p>
  </div>
  ${slides}
</body>
</html>`;
};

const generateQuiz = (content: string, title: string): string => {
  // Extract potential quiz questions from content
  const questions: Array<{q: string; options: string[]}> = [];
  const lines = content.split('\n');
  
  // Look for numbered items or questions
  let currentQ = '';
  let currentOptions: string[] = [];
  
  lines.forEach(line => {
    if (line.match(/^\d+\.\s+\*\*/) || line.match(/^#{2,3}\s+\d+\./)) {
      if (currentQ) {
        questions.push({ q: currentQ, options: currentOptions.length ? currentOptions : ['Richtig', 'Falsch'] });
      }
      currentQ = line.replace(/^\d+\.\s+\*\*|\*\*$/g, '').replace(/^#{2,3}\s+\d+\.\s*/, '').trim();
      currentOptions = [];
    } else if (line.match(/^[-•]\s+/) && currentQ) {
      currentOptions.push(line.replace(/^[-•]\s+/, '').trim());
    }
  });
  if (currentQ) {
    questions.push({ q: currentQ, options: currentOptions.length ? currentOptions : ['Richtig', 'Falsch'] });
  }

  // If no questions found, create sample questions from content
  if (questions.length === 0) {
    questions.push(
      { q: 'Haben Sie die Lernziele dieses Moduls verstanden?', options: ['Ja, vollständig', 'Größtenteils', 'Teilweise', 'Nein'] },
      { q: 'Welches Konzept war am wichtigsten?', options: ['Konzept A', 'Konzept B', 'Konzept C', 'Alle gleich wichtig'] }
    );
  }

  const quizHtml = questions.slice(0, 10).map((q, i) => `
    <div class="question">
      <h3>Frage ${i + 1}</h3>
      <p class="question-text">${q.q}</p>
      <div class="options">
        ${q.options.map((opt, j) => `
          <label class="option">
            <input type="radio" name="q${i}" value="${j}">
            <span>${opt}</span>
          </label>
        `).join('')}
      </div>
    </div>
  `).join('');

  return `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <title>${title} - Quiz</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #f5f5f5; padding: 40px; max-width: 800px; margin: 0 auto; }
    h1 { color: #1976d2; border-bottom: 3px solid #1976d2; padding-bottom: 15px; }
    .question { background: white; padding: 25px; margin: 20px 0; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .question h3 { color: #1976d2; margin-bottom: 15px; }
    .question-text { font-size: 1.2em; margin-bottom: 20px; }
    .options { display: flex; flex-direction: column; gap: 12px; }
    .option { display: flex; align-items: center; padding: 15px; background: #e3f2fd; border-radius: 8px; cursor: pointer; transition: all 0.2s; }
    .option:hover { background: #bbdefb; transform: translateX(5px); }
    .option input { margin-right: 15px; transform: scale(1.3); }
    .option span { font-size: 1.1em; }
    button { background: #1976d2; color: white; border: none; padding: 15px 40px; font-size: 1.2em; border-radius: 8px; cursor: pointer; margin-top: 30px; }
    button:hover { background: #1565c0; }
    .result { display: none; padding: 20px; background: #c8e6c9; border-radius: 8px; margin-top: 20px; font-size: 1.2em; }
  </style>
</head>
<body>
  <h1>📝 ${title}</h1>
  <p>Beantworten Sie die folgenden Fragen:</p>
  <form id="quiz">
    ${quizHtml}
    <button type="button" onclick="checkQuiz()">Auswertung</button>
    <div class="result" id="result"></div>
  </form>
  <script>
    function checkQuiz() {
      const form = document.getElementById('quiz');
      const result = document.getElementById('result');
      const questions = form.querySelectorAll('.question');
      let answered = 0;
      questions.forEach((q, i) => {
        const checked = q.querySelector('input:checked');
        if (checked) answered++;
      });
      result.style.display = 'block';
      result.innerHTML = '✅ Sie haben ' + answered + ' von ' + questions.length + ' Fragen beantwortet.';
    }
  </script>
</body>
</html>`;
};

const generateInteractive = (content: string, title: string): string => {
  return `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <title>${title} - Interaktiv</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); min-height: 100vh; padding: 40px; }
    .container { max-width: 900px; margin: 0 auto; background: white; border-radius: 20px; padding: 40px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
    h1 { color: #667eea; text-align: center; }
    .section { margin: 30px 0; padding: 25px; background: #f8f9fa; border-radius: 12px; border-left: 5px solid #667eea; }
    .section h2 { color: #764ba2; cursor: pointer; }
    .section-content { display: none; margin-top: 15px; line-height: 1.8; }
    .section.active .section-content { display: block; }
    .section h2:before { content: '▶ '; font-size: 0.8em; }
    .section.active h2:before { content: '▼ '; }
    .progress { height: 8px; background: #e0e0e0; border-radius: 4px; margin: 20px 0; }
    .progress-bar { height: 100%; background: linear-gradient(90deg, #667eea, #764ba2); border-radius: 4px; transition: width 0.5s; }
    .note { background: #fff3cd; padding: 15px; border-radius: 8px; margin: 15px 0; }
    .highlight { background: #e3f2fd; padding: 15px; border-radius: 8px; margin: 15px 0; }
  </style>
</head>
<body>
  <div class="container">
    <h1>🎓 ${title}</h1>
    <div class="progress"><div class="progress-bar" id="progress" style="width: 0%"></div></div>
    <p id="progress-text">Klicken Sie auf die Abschnitte um sie zu öffnen</p>
    ${content.split(/(?=^#{1,3} )/gm).filter(s => s.trim()).map((section, i) => {
      const lines = section.trim().split('\n');
      const heading = lines[0]?.replace(/^#+\s*/, '') || `Abschnitt ${i + 1}`;
      const body = markdownToHtml(lines.slice(1).join('\n'));
      return `
    <div class="section" data-index="${i}">
      <h2 onclick="toggleSection(this.parentElement)">${heading}</h2>
      <div class="section-content">${body}</div>
    </div>`;
    }).join('')}
  </div>
  <script>
    let opened = new Set();
    const total = document.querySelectorAll('.section').length;
    function toggleSection(el) {
      el.classList.toggle('active');
      if (el.classList.contains('active')) {
        opened.add(el.dataset.index);
      }
      const pct = Math.round((opened.size / total) * 100);
      document.getElementById('progress').style.width = pct + '%';
      document.getElementById('progress-text').textContent = pct + '% abgeschlossen';
    }
  </script>
</body>
</html>`;
};

const generateVideo = (content: string, title: string): string => {
  // Create a video script format
  const sections = content.split(/(?=^#{1,3} )/gm).filter(s => s.trim());
  
  return `# VIDEO-SKRIPT: ${title}
Generiert am: ${new Date().toLocaleDateString('de-DE')}

================================================================================
PRODUKTIONSHINWEISE
================================================================================
- Gesamtlänge: ca. ${Math.ceil(sections.length * 2)} Minuten
- Stil: Professionell, informativ
- Zielgruppe: Techniker / Fachpersonal

================================================================================
INTRO (ca. 30 Sekunden)
================================================================================
[EINBLENDUNG: Titel "${title}"]
[MUSIK: Professioneller Jingle]

SPRECHER:
"Willkommen zu diesem Schulungsvideo zum Thema ${title}."

================================================================================
HAUPTTEIL
================================================================================

${sections.map((section, i) => {
  const lines = section.trim().split('\n');
  const heading = lines[0]?.replace(/^#+\s*/, '') || `Abschnitt ${i + 1}`;
  const body = lines.slice(1).join('\n').replace(/\*\*/g, '').replace(/\*/g, '');
  return `
--- SZENE ${i + 1}: ${heading} ---
[ZEIT: ca. ${Math.ceil(body.length / 500)} Minuten]
[VISUALS: Grafik/Animation zu "${heading}"]

SPRECHER:
${body.split('\n').filter(l => l.trim()).map(l => `"${l.trim()}"`).join('\n')}
`;
}).join('\n')}

================================================================================
OUTRO (ca. 30 Sekunden)
================================================================================
[EINBLENDUNG: Zusammenfassung]

SPRECHER:
"Das waren die wichtigsten Punkte zu ${title}. 
Bei Fragen wenden Sie sich an Ihre Ansprechpartner."

[MUSIK: Outro-Jingle]
[EINBLENDUNG: Logo / Kontaktdaten]

--- ENDE DES SKRIPTS ---
`;
};

const downloadContent = (content: string, title: string, format: string) => {
  const safeFilename = title.replace(/[^a-zA-Z0-9äöüÄÖÜß\-_\s]/g, '').replace(/\s+/g, '_');
  const filename = `${safeFilename}_${format}`;
  
  switch (format) {
    case 'Slides':
      downloadFile(generateSlides(content, title), filename, 'text/html', 'html');
      break;
    case 'Quiz':
      downloadFile(generateQuiz(content, title), filename, 'text/html', 'html');
      break;
    case 'Interactive':
      downloadFile(generateInteractive(content, title), filename, 'text/html', 'html');
      break;
    case 'Video':
      downloadFile(generateVideo(content, title), filename, 'text/markdown', 'md');
      break;
    case 'PDF':
      // Open print dialog for PDF
      const pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write(`<!DOCTYPE html>
<html><head><title>${title}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
  h1, h2, h3 { color: #1976d2; margin-top: 25px; }
  table { border-collapse: collapse; width: 100%; margin: 20px 0; }
  th, td { border: 1px solid #ddd; padding: 12px; }
  th { background: #1976d2; color: white; }
  code { background: #f5f5f5; padding: 2px 6px; border-radius: 4px; }
  .header { border-bottom: 2px solid #1976d2; margin-bottom: 30px; padding-bottom: 20px; }
</style></head>
<body>
  <div class="header"><h1>${title}</h1><p>Erstellt am: ${new Date().toLocaleDateString('de-DE')}</p></div>
  ${markdownToHtml(content)}
</body></html>`);
        pdfWindow.document.close();
        pdfWindow.print();
      }
      break;
    case 'HTML':
      const htmlContent = `<!DOCTYPE html>
<html lang="de"><head><meta charset="UTF-8"><title>${title}</title>
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; line-height: 1.7; background: #fafafa; }
  h1, h2, h3 { color: #1976d2; }
  h1 { border-bottom: 3px solid #1976d2; padding-bottom: 15px; }
  table { border-collapse: collapse; width: 100%; margin: 20px 0; background: white; }
  th, td { border: 1px solid #ddd; padding: 12px; }
  th { background: #1976d2; color: white; }
  tr:nth-child(even) { background: #f9f9f9; }
  code { background: #e8e8e8; padding: 2px 8px; border-radius: 4px; font-family: monospace; }
  pre { background: #263238; color: #aed581; padding: 20px; border-radius: 8px; overflow-x: auto; }
  blockquote { border-left: 4px solid #1976d2; margin: 20px 0; padding: 15px 25px; background: #e3f2fd; }
  .content { background: white; padding: 40px; border-radius: 12px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
  .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; text-align: center; }
</style></head>
<body><div class="content"><h1>${title}</h1>${markdownToHtml(content)}</div>
<div class="footer">Erstellt mit DZ Content Factory am ${new Date().toLocaleDateString('de-DE')}</div></body></html>`;
      downloadFile(htmlContent, filename, 'text/html', 'html');
      break;
    case 'DITA':
      const ditaContent = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE topic PUBLIC "-//OASIS//DTD DITA Topic//EN" "topic.dtd">
<topic id="${safeFilename}">
  <title>${title}</title>
  <shortdesc>Automatisch generiertes Dokument</shortdesc>
  <body>
    ${content.split('\n\n').map(p => `<p>${p.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`).join('\n    ')}
  </body>
</topic>`;
      downloadFile(ditaContent, filename, 'application/xml', 'dita');
      break;
    case 'S1000D':
      downloadFile(`<!-- S1000D Data Module: ${title} -->\n${content}`, filename, 'application/xml', 'xml');
      break;
    default:
      // Markdown
      downloadFile(content, filename, 'text/markdown', 'md');
  }
};

function WorkflowExecutor() {
  const { isAuthenticated, canUseApp, getRemainingUsage, refreshUsage, trackUsage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [contentType, setContentType] = useState<ContentType>('documentation');
  const [workflowId, setWorkflowId] = useState('content-factory');
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showEquipmentManager, setShowEquipmentManager] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info' | 'warning'}>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  // Form state per content type
  const [docData, setDocData] = useState<DocumentationData>(defaultDocumentation);
  const [trainingData, setTrainingData] = useState<TrainingData>(defaultTraining);
  const [simData, setSimData] = useState<SimulationData>(defaultSimulation);
  const [templateData, setTemplateData] = useState<TemplatesData>(defaultTemplates);

  const suggestedWorkflowIds: Record<ContentType, string> = {
    documentation: 'content-factory',
    training: 'content-factory',
    simulation: 'content-factory',
    templates: 'content-factory',
  };

  // Content type change handler (used by UI toggle)
  const _handleContentTypeChange = (_: unknown, next: ContentType | null) => {
    if (!next) return;
    setContentType(next);
    setErrors({});
    setWorkflowId(suggestedWorkflowIds[next]);
  };
  // Suppress unused warning - function is available for future use
  void _handleContentTypeChange;

  const getFormData = (): object => {
    switch (contentType) {
      case 'documentation':
        return { ...docData, metadata: { manufacturer: docData.manufacturer, model: docData.model } };
      case 'training':
        return trainingData;
      case 'simulation':
        return { scenario: simData.scenario, parameters: { duration: simData.duration, resolution: simData.resolution }, synthesize: simData.synthesize };
      case 'templates':
        return templateData;
    }
  };

  const validateForm = (): boolean => {
    let validationErrors: ValidationErrors = {};
    switch (contentType) {
      case 'documentation':
        validationErrors = validateDocumentation(docData);
        break;
      case 'training':
        validationErrors = validateTraining(trainingData);
        break;
      case 'simulation':
        validationErrors = validateSimulation(simData);
        break;
      case 'templates':
        validationErrors = validateTemplates(templateData);
        break;
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const [workflowResult, setWorkflowResult] = useState<unknown>(null);
  const [generatedContents, setGeneratedContents] = useState<GeneratedContent[]>([]);
  const [selectedContent, setSelectedContent] = useState<GeneratedContent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [selectedPreviewFormat, setSelectedPreviewFormat] = useState<string>('');

  // Handle workflow result from bulk import
  useEffect(() => {
    const state = location.state as any;
    if (state?.workflowResult && state?.bulkImport) {
      // Extract content from workflow result
      const result = state.workflowResult;
      
      // Check if we have multi-format response from n8n
      let multiFormat: FormatContent[] | undefined = undefined;
      let content: string = '';
      
      if (Array.isArray(result) && result.length > 0 && typeof result[0] === 'object' && 'format' in result[0]) {
        multiFormat = result.map((file: any) => ({
          format: file.format,
          filename: file.filename,
          content: file.content || '',
          mimeType: file.mimeType || 'text/plain'
        }));
        content = multiFormat?.[0]?.content || JSON.stringify(result, null, 2);
      } else if (result.files && Array.isArray(result.files)) {
        // New format: n8n returns array of files with format, filename, content
        multiFormat = result.files.map((file: any) => ({
          format: file.format,
          filename: file.filename,
          content: file.content || '',
          mimeType: file.mimeType || 'text/plain'
        }));
        // Use first format's content as default display
        content = multiFormat?.[0]?.content || JSON.stringify(result, null, 2);
      } else {
        // Legacy format: single content string
        // Helper to extract text from Mistral format, excluding thinking blocks
        const extractTextOnly = (data: any[]): string => {
          const textParts: string[] = [];
          for (const item of data) {
            if (item && typeof item === 'object') {
              // Skip thinking blocks completely
              if (item.type === 'thinking') {
                continue;
              }
              // Only get text from "text" type entries
              if (item.type === 'text' && typeof item.text === 'string') {
                textParts.push(item.text);
              }
            }
          }
          return textParts.length > 0 ? textParts.join('\n\n') : '';
        };
        
        if (typeof result.content === 'string') {
          content = result.content;
        } else if (typeof result.text === 'string') {
          content = result.text;
        } else if (typeof result.response === 'string') {
          content = result.response;
        } else if (Array.isArray(result.data)) {
          content = extractTextOnly(result.data) || JSON.stringify(result.data, null, 2);
        } else if (result.content && typeof result.content === 'object' && Array.isArray(result.content.data)) {
          content = extractTextOnly(result.content.data) || JSON.stringify(result.content.data, null, 2);
        } else {
          content = JSON.stringify(result, null, 2);
        }
      }
      
      // Create generated content entry
      const newContent: GeneratedContent = {
        id: Date.now().toString(),
        contentType: 'documentation',
        title: `Bulk-Dokumentation: ${state.sensorCount} Sensoren`,
        content,
        formats: ['DITA', 'AASX', 'VDI 2770'],
        generatedAt: new Date().toISOString(),
        metadata: result,
        multiFormat, // Add multi-format data if available
      };
      
      setGeneratedContents(prev => [newContent, ...prev]);
      setWorkflowResult(result);
      
      // Save to history database
      createHistoryEntry({
        contentType: 'documentation',
        title: newContent.title,
        inputData: {
          bulkImport: true,
          sensorCount: state.sensorCount,
        },
        outputData: {
          content: newContent.content,
          formats: newContent.formats,
        },
        format: 'markdown',
        metadata: {
          bulkImport: true,
          sensorCount: state.sensorCount,
        },
      }).catch((error) => {
        console.warn('Failed to save bulk workflow to history:', error);
      });
      
      setSnackbar({ 
        open: true, 
        message: `✅ Bulk-Dokumentation für ${state.sensorCount} Sensoren erfolgreich erstellt!`, 
        severity: 'success' 
      });
      
      // Clear location state
      navigate('/app', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Get formats based on content type
  const getFormats = (): string[] => {
    switch (contentType) {
      case 'training':
        return trainingData.format;
      case 'documentation':
        return docData.outputs;
      default:
        return ['Markdown', 'HTML', 'PDF'];
    }
  };

  // getRemainingUsage is used by canUseApp internally
  void getRemainingUsage;

  // Generate title based on content type
  const generateTitle = (): string => {
    switch (contentType) {
      case 'documentation':
        return `Dokumentation: ${docData.sensorId}`;
      case 'training':
        return `Schulung: ${trainingData.topic}`;
      case 'simulation':
        return `Simulation: ${simData.scenario}`;
      case 'templates':
        return `Template: ${templateData.templateType}`;
    }
  };

  const handleExecuteWorkflow = async () => {
    // Check usage limits
    if (!canUseApp()) {
      setShowUpgradeDialog(true);
      return;
    }

    if (!validateForm()) {
      setSnackbar({ open: true, message: 'Bitte alle Pflichtfelder ausfüllen', severity: 'warning' });
      return;
    }

    // Track usage BEFORE executing workflow
    const usageResult = await trackUsage(contentType, getFormData() as Record<string, unknown>);
    if (!usageResult.success) {
      if (usageResult.error === 'Usage limit reached') {
        setShowUpgradeDialog(true);
      } else {
        setSnackbar({ open: true, message: usageResult.error || 'Usage tracking failed', severity: 'error' });
      }
      return;
    }

    setIsExecuting(true);
    console.log('[UI] Starting workflow execution...');
    const startTime = Date.now();
    
    try {
      // Special handling for simulation - use backend API
      if (contentType === 'simulation' && simData.machineTypeId) {
        console.log('[UI] Generating simulation via Backend API...');
        
        // Parse duration and resolution
        const parseDuration = (dur: string): number => {
          const match = dur.match(/(\d+)(min|h|s)/);
          if (!match) return 900;
          const value = parseInt(match[1]);
          switch (match[2]) {
            case 'h': return value * 3600;
            case 'min': return value * 60;
            case 's': return value;
            default: return 900;
          }
        };
        
        const parseResolution = (res: string): number => {
          const match = res.match(/(\d+)(ms|s)/);
          if (!match) return 1000;
          const value = parseInt(match[1]);
          return match[2] === 'ms' ? value : value * 1000;
        };
        
        // Map format names to API format codes
        const formatMap: Record<string, 'json' | 'json-ld' | 'aasx' | 'csv (vdi 2770)' | 'xml' | 'opcua' | 'mqtt/sparkplug b' | 'parquet'> = {
          'JSON': 'json',
          'JSON-LD': 'json-ld',
          'AASX': 'aasx',
          'CSV (VDI 2770)': 'csv (vdi 2770)',
          'XML': 'xml',
          'OPC UA NodeSet': 'opcua',
          'MQTT/Sparkplug B': 'mqtt/sparkplug b',
          'Parquet': 'parquet',
        };
        
        // Generate simulation for each selected format
        const simulationResults: Record<string, string> = {};
        
        for (const format of simData.outputFormats) {
          const apiFormat = formatMap[format] || 'json';
          const result = await generateSimulation({
            machineTypeId: simData.machineTypeId,
            durationSeconds: parseDuration(simData.duration),
            resolutionMs: parseResolution(simData.resolution),
            scenario: simData.scenario as 'normal' | 'anomaly' | 'failure' | 'startup' | 'shutdown' | 'corner-case' | 'cyber-physical-attack' | 'cascading-drift' | 'ood-detection',
            format: apiFormat,
            fidelity: simData.fidelity,
            targetSystem: simData.targetSystem,
            anomalyType: simData.anomalyType,
            outputFormats: simData.outputFormats,
            synthesize: simData.synthesize, // Pass the synthesize parameter to backend
          });
          simulationResults[format] = typeof result === 'string' ? result : JSON.stringify(result, null, 2);
        }
        
        const duration = ((Date.now() - startTime) / 1000).toFixed(1);
        console.log(`[UI] Simulation generated in ${duration}s`);
        
        // Create content entry with simulation data
        const newContent: GeneratedContent = {
          id: Date.now().toString(),
          contentType: 'simulation',
          title: `Simulation: ${simData.scenario} - ${simData.duration} @ ${simData.resolution}`,
          content: simulationResults['JSON'] || Object.values(simulationResults)[0],
          formats: simData.outputFormats,
          generatedAt: new Date().toISOString(),
          metadata: {
            simulationResults,
            machineTypeId: simData.machineTypeId,
            duration: simData.duration,
            resolution: simData.resolution,
            scenario: simData.scenario,
            fidelity: simData.fidelity,
            targetSystem: simData.targetSystem,
            anomalyType: simData.anomalyType,
          },
        };
        
        setGeneratedContents(prev => [newContent, ...prev]);
        
        // Save to history database
        try {
          await createHistoryEntry({
            contentType: 'simulation',
            title: newContent.title,
            inputData: {
              machineTypeId: simData.machineTypeId,
              duration: simData.duration,
              resolution: simData.resolution,
              scenario: simData.scenario,
              fidelity: simData.fidelity,
              targetSystem: simData.targetSystem,
              anomalyType: simData.anomalyType,
              outputFormats: simData.outputFormats,
            },
            outputData: {
              content: newContent.content,
              simulationResults,
            },
            format: 'json',
            metadata: { generationTimeMs: Date.now() - startTime },
          });
        } catch (historyError) {
          console.warn('Failed to save to history:', historyError);
        }
        
        setExecutionId(new Date().toISOString());
        setSnackbar({ open: true, message: `Simulationsdaten generiert! (${duration}s)`, severity: 'success' });
        return;
      }
      
      // Default: use n8n webhook for other content types
      const data = { contentType, ...getFormData() };
      console.log('[UI] Sending request:', data);
      const result = await executeWorkflow(workflowId, data);
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(`[UI] Workflow completed in ${duration}s:`, result);
      setWorkflowResult(result);
      
      // Extract content from result - handle various n8n response formats
      const resultData = result as Record<string, unknown>;
      
      // Helper to extract text from Mistral/Magistral response format
      const extractMistralContent = (data: unknown): string | null => {
        if (!Array.isArray(data)) return null;
        
        // Mistral returns array of {type, text, thinking} objects
        // We want all "text" type entries, excluding "thinking"
        const textParts: string[] = [];
        for (const item of data) {
          if (item && typeof item === 'object') {
            const obj = item as Record<string, unknown>;
            // Only get text from "text" type entries (not thinking)
            if (obj.type === 'text' && typeof obj.text === 'string') {
              textParts.push(obj.text);
            }
            // IMPORTANT: Skip thinking blocks completely
            if (obj.type === 'thinking') {
              continue;
            }
          }
        }
        return textParts.length > 0 ? textParts.join('\n\n') : null;
      };
      
      // Check if we have multi-format response from n8n
      let multiFormat: FormatContent[] | undefined = extractMultiFormatFromResult(result);
      let content: string = '';

      if (multiFormat && multiFormat.length > 0) {
        console.log('[UI] Extracted multi-format payload:', multiFormat.map(f => ({ format: f.format, filename: f.filename })));
      }

      if (multiFormat && multiFormat.length > 0) {
        content = multiFormat[0].content || JSON.stringify(result, null, 2);
      } else {
        // Try to extract from various response formats, prioritizing Mistral/Magistral format
        let extracted: string | null = null;
        
        // Check if result is an array (n8n "All incoming items" mode)
        if (Array.isArray(result)) {
          for (const item of result) {
            if (item && typeof item === 'object') {
              const itemObj = item as Record<string, unknown>;
              if (itemObj.content && typeof itemObj.content === 'object') {
                const contentObj = itemObj.content as Record<string, unknown>;
                if (Array.isArray(contentObj.data)) {
                  extracted = extractMistralContent(contentObj.data);
                  if (extracted) break;
                }
              }
            }
          }
        }
        
        // Legacy format: single content string
        if (!extracted && typeof resultData.content === 'string') {
          content = decodeXmlEntitiesIfNeeded(resultData.content);
        } else if (!extracted && typeof resultData.text === 'string') {
          content = decodeXmlEntitiesIfNeeded(resultData.text);
        } else if (!extracted && typeof resultData.response === 'string') {
          content = decodeXmlEntitiesIfNeeded(resultData.response);
        } else if (!extracted && Array.isArray(resultData.data)) {
          // Mistral format: {data: [{type: "text", text: "..."}, ...]}
          extracted = extractMistralContent(resultData.data);
          content = extracted || JSON.stringify(resultData.data, null, 2);
        } else if (!extracted && resultData.content && typeof resultData.content === 'object') {
          // Handle nested content.data format
          const contentObj = resultData.content as Record<string, unknown>;
          if (Array.isArray(contentObj.data)) {
            extracted = extractMistralContent(contentObj.data);
            content = extracted || JSON.stringify(contentObj.data, null, 2);
          } else if (typeof contentObj.content === 'string') {
            content = decodeXmlEntitiesIfNeeded(contentObj.content);
          } else {
            content = JSON.stringify(contentObj, null, 2);
          }
        } else if (!extracted && resultData.data && typeof resultData.data === 'object') {
          // Other nested {data: {...}} format
          const dataObj = resultData.data as Record<string, unknown>;
          if (typeof dataObj.content === 'string') {
            content = decodeXmlEntitiesIfNeeded(dataObj.content);
          } else if (typeof dataObj.text === 'string') {
            content = decodeXmlEntitiesIfNeeded(dataObj.text);
          } else if (typeof dataObj.response === 'string') {
            content = decodeXmlEntitiesIfNeeded(dataObj.response);
          } else {
            content = JSON.stringify(dataObj, null, 2);
          }
        } else if (extracted) {
          content = extracted;
        } else {
          content = JSON.stringify(result, null, 2);
        }
      }
      
      // Create new generated content entry
      const newContent: GeneratedContent = {
        id: Date.now().toString(),
        contentType,
        title: generateTitle(),
        content,
        formats: getFormats(),
        generatedAt: new Date().toISOString(),
        metadata: resultData,
        multiFormat, // Add multi-format support
      };
      
      setGeneratedContents(prev => [newContent, ...prev]);
      
      // Save to history database
      try {
        await createHistoryEntry({
          contentType,
          title: newContent.title,
          inputData: getFormData() as Record<string, unknown>,
          outputData: {
            content: newContent.content,
            formats: newContent.formats,
            multiFormat: multiFormat, // Save multi-format data
          },
          format: 'markdown',
          metadata: { 
            generationTimeMs: Date.now() - startTime,
            workflowId,
          },
        });
      } catch (historyError) {
        console.warn('Failed to save to history:', historyError);
      }
      
      setExecutionId(new Date().toISOString());
      setSnackbar({ open: true, message: `Content erfolgreich generiert! (${duration}s)`, severity: 'success' });
    } catch (error: unknown) {
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      console.error(`[UI] Error after ${duration}s:`, error);
      
      // Better error messages
      let errorMessage = 'Workflow konnte nicht gestartet werden';
      if (error && typeof error === 'object' && 'code' in error) {
        const axiosError = error as { code?: string; message?: string; response?: { status?: number; data?: { error?: string; suggestion?: string } } };
        if (axiosError.code === 'ERR_NETWORK') {
          errorMessage = 'Netzwerkfehler - Prüfe ob Backend Server läuft (npm run dev im backend/ Ordner)';
        } else if (axiosError.code === 'ECONNABORTED') {
          errorMessage = `Timeout nach ${duration}s - Datensatz zu groß oder Server überlastet. Versuche mit kleinerer Auflösung oder kürzerer Dauer.`;
        } else if (axiosError.response?.status === 404) {
          errorMessage = 'Nicht gefunden - Prüfe Maschinentyp-Auswahl oder Workflow ID';
        } else if (axiosError.response?.status === 500) {
          errorMessage = 'Server-Fehler - Prüfe Backend-Logs';
        } else if (axiosError.response?.status === 400) {
          // Handle specific backend validation errors
          const backendError = axiosError.response.data as { error?: string; suggestion?: string; totalPoints?: number; maxPoints?: number };
          if (backendError?.error?.includes('Dataset too large')) {
            errorMessage = `Datensatz zu groß: ${backendError.totalPoints?.toLocaleString()} Datenpunkte. ${backendError.suggestion || 'Reduziere Dauer oder erhöhe Auflösung.'}`;
          } else if (backendError?.error?.includes('No sensors found')) {
            errorMessage = 'Keine Sensoren für diesen Maschinentyp gefunden - Wähle anderen Maschinentyp oder füge Sensoren hinzu.';
          } else {
            errorMessage = backendError?.error || 'Ungültige Anfrage';
          }
        }
      } else if (error instanceof Error) {
        // Handle direct Error objects (like timeout from simulation generation)
        if (error.message.includes('timeout') || error.message.includes('too large')) {
          errorMessage = `Timeout: Datensatz zu groß für die Generierung. Versuche mit:\n• Gröberer Auflösung (1s statt 100ms)\n• Kürzerer Dauer (1h statt 24h)\n• Weniger Sensoren`;
        } else if (error.message.includes('No sensors found')) {
          errorMessage = 'Keine Sensoren für diesen Maschinentyp gefunden';
        }
      }
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setIsExecuting(false);
      // Refresh usage count from server
      refreshUsage();
    }
  };

  const handleOpenContent = (content: GeneratedContent) => {
    setSelectedContent(content);
    if (content.multiFormat && content.multiFormat.length > 0) {
      setSelectedPreviewFormat(content.multiFormat[0]?.format || '');
    } else {
      setSelectedPreviewFormat(content.formats?.[0] || '');
    }
    setDialogOpen(true);
  };

  const handleDownload = (content: GeneratedContent, format: string) => {
    // Check if we have multi-format data (from bulk import)
    if (content.multiFormat && content.multiFormat.length > 0) {
      const formatData = pickMultiFormatContent(content.multiFormat, format);
      if (formatData) {
        // Direct download of pre-generated format
        const extension = formatData.filename.split('.').pop() || 'txt';
        const filenameWithoutExt = formatData.filename.replace(/\.[^/.]+$/, '');
        const cleaned = decodeXmlEntitiesIfNeeded(formatData.content);
        downloadFile(cleaned, filenameWithoutExt, formatData.mimeType, extension);
        setSnackbar({ open: true, message: `${format}-Download gestartet`, severity: 'info' });
        return;
      }
    }
    
    // For simulation content, use the pre-generated format-specific data
    if (content.contentType === 'simulation' && content.metadata?.simulationResults) {
      const results = content.metadata.simulationResults as Record<string, string>;
      const simulationData = results[format];
      
      if (simulationData) {
        const title = content.title.replace(/[^a-zA-Z0-9äöüÄÖÜß\-_\s]/g, '').replace(/\s+/g, '_');
        
        switch (format) {
          case 'JSON':
            downloadFile(simulationData, title, 'application/json', 'json');
            break;
          case 'XML':
            downloadFile(simulationData, title, 'application/xml', 'xml');
            break;
          case 'OPC UA NodeSet':
            downloadFile(simulationData, `${title}_NodeSet2`, 'application/xml', 'xml');
            break;
          case 'CSV':
            downloadFile(simulationData, title, 'text/csv', 'csv');
            break;
          default:
            downloadFile(simulationData, title, 'application/octet-stream', 'dat');
        }
        
        setSnackbar({ open: true, message: `${format}-Download gestartet`, severity: 'info' });
        return;
      }
    }
    
    // Default download for other content types
    downloadContent(content.content, content.title, format);
    setSnackbar({ open: true, message: `${format}-Download gestartet`, severity: 'info' });
  };

  // For synchronous webhook responses, we don't need polling
  const { data: executionStatus } = useQuery(
    ['workflowStatus', executionId],
    () => executionId ? Promise.resolve({ status: 'completed', data: workflowResult }) : null,
    { 
      enabled: !!executionId,
      refetchInterval: false, // No polling needed for sync webhooks
      refetchIntervalInBackground: false,
    }
  );

  // Workflow type cards configuration
  const workflowCards = [
    {
      value: 'documentation' as ContentType,
      title: 'Manuelle Sensor-Doku',
      description: 'Einzelne Sensoren manuell dokumentieren',
      icon: <SettingsIcon sx={{ fontSize: 32 }} />,
      gradient: 'linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 150, 199, 0.05))',
      borderColor: 'rgba(0, 180, 216, 0.4)',
    },
    {
      value: 'csv-import' as any,
      title: 'Bulk Dokumentation',
      description: 'Hunderte Sensoren per CSV importieren',
      icon: <UploadFileIcon sx={{ fontSize: 32 }} />,
      gradient: 'linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 150, 199, 0.05))',
      borderColor: 'rgba(0, 180, 216, 0.4)',
      isLink: true,
      linkTo: '/sensor-import',
    },
    {
      value: 'training' as ContentType,
      title: 'Schulungsmaterial',
      description: 'Interaktive Lernmaterialien erstellen',
      icon: <SchoolIcon sx={{ fontSize: 32 }} />,
      gradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15), rgba(13, 148, 136, 0.05))',
      borderColor: 'rgba(20, 184, 166, 0.4)',
    },
    {
      value: 'simulation' as ContentType,
      title: 'Simulationsdaten',
      description: 'Synthese-Content & Testdaten generieren',
      icon: <TimelineIcon sx={{ fontSize: 32 }} />,
      gradient: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15), rgba(234, 88, 12, 0.05))',
      borderColor: 'rgba(249, 115, 22, 0.4)',
    },
    {
      value: 'templates' as ContentType,
      title: 'Templates',
      description: 'Standardisierte Vorlagen (DITA, S1000D)',
      icon: <WidgetsIcon sx={{ fontSize: 32 }} />,
      gradient: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(139, 92, 246, 0.05))',
      borderColor: 'rgba(168, 85, 247, 0.4)',
    },
  ];

  return (
    <Box className="industrial-bg" sx={{ minHeight: 'calc(100vh - 64px)', py: 4 }}>
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12} lg={9} component="div">
            {/* Header Section */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#e2e8f0',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 20px rgba(0, 180, 216, 0.3)',
                    }}
                  >
                    <SettingsIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  Digital Twin Content Studio
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ ml: 8 }}>
                  Wähle einen Content-Typ und führe den passenden Workflow aus.
                </Typography>
              </Box>
              
              {/* Equipment Manager Button */}
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setShowEquipmentManager(true)}
                sx={{
                  borderColor: 'rgba(249, 115, 22, 0.5)',
                  color: '#f97316',
                  '&:hover': {
                    borderColor: '#f97316',
                    background: 'rgba(249, 115, 22, 0.1)',
                  },
                }}
              >
                Equipment verwalten
              </Button>
            </Box>

            {/* Workflow Type Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {workflowCards.map((card) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.value}>
                  <Card
                    onClick={() => {
                      if ((card as any).isLink && (card as any).linkTo) {
                        navigate((card as any).linkTo);
                      } else {
                        const contentTypeValue = card.value as ContentType;
                        setContentType(contentTypeValue);
                        setErrors({});
                        if (contentTypeValue in suggestedWorkflowIds) {
                          setWorkflowId(suggestedWorkflowIds[contentTypeValue]);
                        }
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      background: (card as any).isLink ? 'rgba(16, 42, 67, 0.5)' : (contentType === card.value ? card.gradient : 'rgba(16, 42, 67, 0.5)'),
                      border: (card as any).isLink 
                        ? '1px solid rgba(0, 180, 216, 0.1)'
                        : (contentType === card.value 
                          ? `2px solid ${card.borderColor}` 
                          : '1px solid rgba(0, 180, 216, 0.1)'),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: card.gradient,
                        border: `2px solid ${card.borderColor}`,
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 40px rgba(0, 0, 0, 0.3), 0 0 30px ${card.borderColor.replace('0.4', '0.2')}`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Box 
                        sx={{ 
                          color: contentType === card.value ? card.borderColor.replace('0.4', '1') : 'text.secondary',
                          mb: 1.5,
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600, 
                          color: contentType === card.value ? '#e2e8f0' : 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ display: 'block', lineHeight: 1.4 }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Main Form Card */}
            <Paper 
              sx={{ 
                p: 4,
                background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: 'linear-gradient(90deg, #00b4d8, #14b8a6, #f97316)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Chip 
                  label={workflowCards.find(c => c.value === contentType)?.title || 'Workflow'} 
                  sx={{ 
                    background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2), rgba(20, 184, 166, 0.2))',
                    border: '1px solid rgba(0, 180, 216, 0.3)',
                    fontWeight: 600,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Workflow ID: {workflowId}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

            {/* Category-specific forms */}
            {contentType === 'documentation' && (
              <DocumentationForm data={docData} onChange={setDocData} errors={errors} />
            )}
            {contentType === 'training' && (
              <TrainingForm data={trainingData} onChange={setTrainingData} errors={errors} />
            )}
            {contentType === 'simulation' && (
              <SimulationForm data={simData} onChange={setSimData} errors={errors} />
            )}
            {contentType === 'templates' && (
              <TemplatesForm data={templateData} onChange={setTemplateData} errors={errors} />
            )}

              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(0, 180, 216, 0.1)' }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleExecuteWorkflow}
                    disabled={!workflowId || isExecuting}
                    size="large"
                    fullWidth
                    sx={{ 
                      py: 1.5,
                      fontSize: '1rem',
                      background: isExecuting 
                        ? 'linear-gradient(135deg, #f97316, #ea580c)'
                        : 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                      boxShadow: isExecuting
                        ? '0 4px 20px rgba(249, 115, 22, 0.3)'
                        : '0 4px 20px rgba(0, 180, 216, 0.3)',
                      '&:hover': {
                        background: isExecuting 
                          ? 'linear-gradient(135deg, #fb923c, #f97316)'
                          : 'linear-gradient(135deg, #00d9ff, #2dd4bf)',
                      },
                    }}
                    startIcon={isExecuting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isExecuting ? 'Wird generiert...' : 'Workflow ausführen'}
                  </Button>
                  {contentType === 'documentation' && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setDocData(defaultDocumentation);
                        setErrors({});
                      }}
                      size="large"
                      sx={{ 
                        py: 1.5,
                        fontSize: '1rem',
                        minWidth: '200px',
                        borderColor: 'rgba(0, 180, 216, 0.4)',
                        color: '#00b4d8',
                        '&:hover': {
                          borderColor: '#00b4d8',
                          background: 'rgba(0, 180, 216, 0.1)',
                        },
                      }}
                    >
                      Nächster Sensor
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        
          {/* Status & Generated Content Panel */}
          <Grid item xs={12} lg={3} component="div">
            {/* Status Card */}
            <Paper 
              sx={{ 
                p: 3, 
                mb: 3,
                background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box 
                  sx={{ 
                    width: 10, 
                    height: 10, 
                    borderRadius: '50%',
                    background: isExecuting 
                      ? '#f97316' 
                      : executionStatus 
                        ? '#10b981' 
                        : 'rgba(148, 163, 184, 0.5)',
                    boxShadow: isExecuting 
                      ? '0 0 10px rgba(249, 115, 22, 0.5)' 
                      : executionStatus 
                        ? '0 0 10px rgba(16, 185, 129, 0.5)' 
                        : 'none',
                    animation: isExecuting ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  Execution Status
                </Typography>
              </Box>
              
            {isExecuting ? (
              <Card 
                variant="outlined" 
                sx={{ 
                  background: 'rgba(249, 115, 22, 0.1)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <CircularProgress size={20} sx={{ color: '#f97316' }} />
                    <Chip 
                      label="Wird generiert..." 
                      size="small"
                      sx={{ 
                        background: 'rgba(249, 115, 22, 0.2)',
                        color: '#fb923c',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Content-Typ: {contentType}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    ⏳ LLM-Generierung kann bis zu 60s dauern
                  </Typography>
                </CardContent>
              </Card>
            ) : executionStatus ? (
              <Card 
                variant="outlined"
                sx={{ 
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Chip 
                    label={`Status: ${executionStatus.status}`} 
                    size="small" 
                    sx={{ 
                      mb: 1.5,
                      background: 'rgba(16, 185, 129, 0.2)',
                      color: '#34d399',
                      fontWeight: 600,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                    Execution ID: {executionId?.slice(0, 19)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                    Content-Typ: {contentType}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Aktualisiert: {new Date().toLocaleTimeString()}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                py: 3, 
                color: 'text.secondary',
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 2,
              }}>
                <Typography variant="body2">
                  Keine aktive Ausführung
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Generated Content List */}
          <Paper 
            sx={{ 
              p: 3,
              background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}>
              Generierte Dokumente
            </Typography>
            {generatedContents.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4, 
                color: 'text.secondary',
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 2,
              }}>
                <DescriptionIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2">
                  Noch keine Dokumente generiert
                </Typography>
              </Box>
            ) : (
              <List dense>
                {generatedContents.map((item) => {
                  const defaultDownloadFormat =
                    item.multiFormat?.[0]?.format
                    || item.formats?.[0]
                    || 'DITA';
                  return (
                  <ListItem
                    key={item.id}
                    disablePadding
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={() => {
                          const preferredFormat =
                            normalizeFormatName(selectedContent?.id === item.id ? selectedPreviewFormat : '')
                              ? selectedPreviewFormat
                              : defaultDownloadFormat;
                          handleDownload(item, preferredFormat);
                        }}
                        title={`Download als ${selectedContent?.id === item.id ? (selectedPreviewFormat || defaultDownloadFormat) : defaultDownloadFormat}`}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton onClick={() => handleOpenContent(item)}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <DescriptionIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        secondary={
                          <Box component="span">
                            <Typography variant="caption" color="text.secondary" component="span">
                              {new Date(item.generatedAt).toLocaleString('de-DE')}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              {item.formats.slice(0, 3).map((format) => (
                                <Chip
                                  key={format}
                                  label={format}
                                  size="small"
                                  variant="outlined"
                                  sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: '0.65rem' }}
                                  icon={formatIcons[format] as React.ReactElement || <DescriptionIcon />}
                                />
                              ))}
                            </Box>
                          </Box>
                        }
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                      />
                    </ListItemButton>
                  </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>

          {/* History Panel */}
          <Box sx={{ mt: 3 }}>
            <HistoryPanel 
              onSelectItem={(item) => {
                // Convert history item to GeneratedContent format for display
                const outputData = (item.output_data || {}) as Record<string, unknown>;
                const historyMultiFormat = Array.isArray((outputData as any).multiFormat)
                  ? ((outputData as any).multiFormat as any[]).map((f: any) => ({
                      format: String(f?.format ?? ''),
                      filename: String(f?.filename ?? ''),
                      content: String(f?.content ?? ''),
                      mimeType: String(f?.mimeType ?? 'text/plain'),
                    }))
                  : undefined;

                const derivedFormats = historyMultiFormat && historyMultiFormat.length > 0
                  ? historyMultiFormat.map(f => f.format)
                  : (Array.isArray((outputData as any).formats)
                      ? (((outputData as any).formats as any[]).map((x: any) => String(x)))
                      : (item.format ? [item.format] : []));

                const content: GeneratedContent = {
                  id: item.id,
                  contentType: item.content_type as ContentType,
                  title: item.title || 'Ohne Titel',
                  content: typeof (outputData as any).content === 'string' 
                    ? (outputData as any).content 
                    : JSON.stringify(outputData, null, 2),
                  formats: derivedFormats,
                  generatedAt: item.created_at,
                  metadata: item.metadata || {},
                  multiFormat: historyMultiFormat,
                };
                handleOpenContent(content);
              }}
            />
          </Box>
        </Grid>
      </Grid>
      </Container>

      {/* Content Preview Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle 
          component="div"
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: 'linear-gradient(90deg, rgba(0, 180, 216, 0.1), transparent)',
            borderBottom: '1px solid rgba(0, 180, 216, 0.2)',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedContent?.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedContent && new Date(selectedContent.generatedAt).toLocaleString('de-DE')}
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ background: 'rgba(10, 25, 41, 0.5)' }}>
          {selectedContent && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Verfügbare Formate:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(selectedContent.multiFormat && selectedContent.multiFormat.length > 0
                    ? selectedContent.multiFormat.map(f => f.format)
                    : selectedContent.formats
                  ).map((format) => (
                    <Button
                      key={format}
                      variant="outlined"
                      size="small"
                      startIcon={formatIcons[format] as React.ReactElement || <DescriptionIcon />}
                      onClick={() => handleDownload(selectedContent, format)}
                      sx={{
                        borderColor: 'rgba(0, 180, 216, 0.3)',
                        '&:hover': {
                          borderColor: '#00b4d8',
                          background: 'rgba(0, 180, 216, 0.1)',
                        },
                      }}
                    >
                      {format}
                    </Button>
                  ))}
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Vorschau:
              </Typography>
              {selectedContent.multiFormat && selectedContent.multiFormat.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedContent.multiFormat.map((f, idx) => (
                    <Chip
                      key={`${f.format}-${idx}`}
                      label={f.format}
                      color={normalizeFormatName(selectedPreviewFormat) === normalizeFormatName(f.format) ? 'primary' : 'default'}
                      variant={normalizeFormatName(selectedPreviewFormat) === normalizeFormatName(f.format) ? 'filled' : 'outlined'}
                      onClick={() => setSelectedPreviewFormat(f.format)}
                      size="small"
                    />
                  ))}
                </Box>
              )}
              {/* Check if this is structured training content with quiz */}
              {selectedContent.multiFormat && selectedContent.multiFormat.length > 0 ? (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3, 
                    maxHeight: 400, 
                    overflow: 'auto',
                    bgcolor: 'rgba(10, 25, 41, 0.8)',
                    border: '1px solid rgba(0, 180, 216, 0.15)',
                    whiteSpace: 'pre-wrap',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.8rem',
                    lineHeight: 1.7,
                  }}
                >
                  {(() => {
                    const requested = selectedPreviewFormat || selectedContent.multiFormat?.[0]?.format || '';
                    const chosen = pickMultiFormatContent(selectedContent.multiFormat || [], requested);
                    return decodeXmlEntitiesIfNeeded(chosen?.content || '');
                  })()}
                </Paper>
              ) : selectedContent.contentType === 'training' && parseTrainingContent(selectedContent.content) ? (
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {(() => {
                    const trainingData = parseTrainingContent(selectedContent.content)!;
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Training content */}
                        <TrainingDisplay schulung={trainingData.schulung} />
                        
                        {/* Interactive Quiz */}
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QuizIcon color="primary" /> Wissenstest
                        </Typography>
                        <QuizComponent 
                          quiz={trainingData.quiz} 
                          onComplete={(score, total) => {
                            console.log(`Quiz completed: ${score}/${total}`);
                          }}
                        />
                      </Box>
                    );
                  })()}
                </Box>
              ) : (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3, 
                    maxHeight: 400, 
                    overflow: 'auto',
                    bgcolor: 'rgba(10, 25, 41, 0.8)',
                    border: '1px solid rgba(0, 180, 216, 0.15)',
                    whiteSpace: 'pre-wrap',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.8rem',
                    lineHeight: 1.7,
                  }}
                >
                  {typeof selectedContent.content === 'string' 
                    ? selectedContent.content 
                    : JSON.stringify(selectedContent.content, null, 2)}
                </Paper>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            Schließen
          </Button>
          {selectedContent && (
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(selectedContent, 'Markdown')}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            background: snackbar.severity === 'success' 
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(13, 148, 136, 0.95))'
              : snackbar.severity === 'error'
                ? 'linear-gradient(135deg, rgba(244, 63, 94, 0.95), rgba(220, 38, 38, 0.95))'
                : undefined,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Upgrade Dialog */}
      <Dialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(90deg, rgba(0, 180, 216, 0.1), transparent)',
          borderBottom: '1px solid rgba(0, 180, 216, 0.2)',
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            🚀 Upgrade erforderlich
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {isAuthenticated 
              ? 'Sie haben Ihr kostenloses Kontingent aufgebraucht. Kontaktieren Sie uns für Enterprise-Zugang!'
              : 'Sie haben alle 3 kostenlosen Testanfragen verwendet. Registrieren Sie sich oder loggen Sie sich ein, um fortzufahren.'
            }
          </Typography>
          <Box sx={{ 
            p: 3, 
            background: 'rgba(0, 180, 216, 0.1)', 
            borderRadius: 2,
            border: '1px solid rgba(0, 180, 216, 0.2)',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Enterprise Solutions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ✓ Pilot-Projekt ab €15.000<br />
              ✓ Professional ab €5.000/Monat<br />
              ✓ Enterprise ab €15.000/Monat<br />
              ✓ Individuelle Anpassungen
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Durchschnittlicher ROI: 340% im ersten Jahr
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={() => setShowUpgradeDialog(false)} sx={{ color: 'text.secondary' }}>
            Später
          </Button>
          {!isAuthenticated && (
            <Button
              variant="outlined"
              onClick={() => {
                setShowUpgradeDialog(false);
                navigate('/login');
              }}
            >
              Anmelden
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => {
              setShowUpgradeDialog(false);
              navigate(isAuthenticated ? '/#pricing' : '/register');
            }}
            sx={{
              background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
            }}
          >
            {isAuthenticated ? 'Jetzt upgraden' : 'Kostenlos registrieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Equipment Manager Dialog */}
      <EquipmentManager 
        open={showEquipmentManager} 
        onClose={() => setShowEquipmentManager(false)} 
      />
    </Box>
  );
}

// App Header Component for the main app
function AppHeader() {
  const { user, isAuthenticated, logout, getRemainingUsage } = useAuth();
  const remainingUsage = getRemainingUsage();

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar sx={{ px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          <Box 
            component={Link}
            to="/"
            sx={{
              display: 'inline-flex',
              textDecoration: 'none',
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="DZ Content Factory"
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                objectFit: 'cover',
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
              }}
            />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              component={Link}
              to="/"
              sx={{ 
                fontWeight: 700, 
                letterSpacing: '0.02em',
                background: 'linear-gradient(90deg, #e2e8f0, #00d9ff)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textDecoration: 'none',
              }}
            >
              DZ Content Factory
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Digitaler Zwilling Datengenerator
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {remainingUsage !== -1 && (
            <Chip 
              label={`${remainingUsage} Anfragen übrig`}
              size="small" 
              sx={{ 
                background: remainingUsage > 0 
                  ? 'rgba(16, 185, 129, 0.15)'
                  : 'rgba(244, 63, 94, 0.15)',
                border: remainingUsage > 0 
                  ? '1px solid rgba(16, 185, 129, 0.3)'
                  : '1px solid rgba(244, 63, 94, 0.3)',
                color: remainingUsage > 0 ? '#34d399' : '#f43f5e',
                fontWeight: 500,
              }}
            />
          )}
          
          {isAuthenticated ? (
            <>
              <Tooltip title={user?.email || ''}>
                <Chip
                  icon={<PersonIcon />}
                  label={user?.name || 'User'}
                  size="small"
                  sx={{ 
                    background: 'rgba(0, 180, 216, 0.15)',
                    border: '1px solid rgba(0, 180, 216, 0.3)',
                    fontWeight: 500,
                  }}
                />
              </Tooltip>
              <Tooltip title="Abmelden">
                <IconButton onClick={logout} size="small" sx={{ color: 'text.secondary' }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Button
                component={Link}
                to="/login"
                size="small"
                sx={{ color: '#e2e8f0' }}
              >
                Anmelden
              </Button>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                }}
              >
                Registrieren
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

// Main App Page with Header
function MainApp() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <AppHeader />
      <WorkflowExecutor />
    </Box>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider theme={industrialTheme}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CssBaseline />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/impressum" element={<ImpressumPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/app" element={<MainApp />} />
              <Route path="/sensor-import" element={<><AppHeader /><SensorImport /></>} />
              <Route path="/sensor-documentation" element={<><AppHeader /><SensorDocumentation /></>} />
            </Routes>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
